// @flow
import environment from '../environment';
import {fireEventAnalyticsApi, pageAnalyticsApi} from './api';
import {getCookieBasedTest} from '../modules/ab-test/utils';

import type { BagItem, Category, OrderItem, ScrappedProduct, ShoppingCartItem, User } from '../entities';
import type { LocaleState } from '../modules/locale/duck';

export type GTMDataLayer = {
  push: (obj: { [key: string]: string }) => void;
  reset: () => void;
};

export type SegmentAnalytics = {
  identify: (userId: string, extra?: { [key: string]: string }) => void;
  page: (category?: string, properties?: { [key: string]: string }) => void;
  debug: (active?: boolean) => void;
  reset: () => void;
  ready: (cb: () => void) => void;
  track: (eventName: string, properties?: { [key: string]: string }, options?: { [key: string]: any }) => void;
  user: () => {
    anonymousId: (anonymousId?: string) => string;
  };
};

type Product = {
  url: string;
  name: string;
  currency: string;
  price: number;
  quantity: number;
  shipping: number;
  category: string;
  image_url: string;
};

export type TrackingData = {
  gaId: string;
  userReferrer: string;
  landingUrl: string;
  domainCountry: string;
  anonymousId: string;
};

export type Raven = {
  captureException: (error: Error, args?: any) => void;
  config: (dns: string, options?: { [key: string]: any }) => ({ install: () => void });
  setUserContext: (context?: { [key: string]: string }) => void;
};

/*export type Mixpanel = {
  init: (token: string, { [key: string]: any }) => void;
  identify: (id: string) => void;
  reset: () => void;
};*/

export class Analytics {
  _gaReadyCb: ?() => void;
  _analyticsReadyCb: ?() => void;

  get analytics(): SegmentAnalytics {
    return window.analytics;
  }

  _gtag = (...args: any) => {
    window.gtag(...args);
  };

  get Raven(): Raven {
    return window.Raven;
  }

  get googleAnalyticsId(): string {
    return window.gaid || '';
  }

  /*get mixpanel(): Mixpanel {
    return window.mixpanel;
  }*/

  /* COMMON AUTHENTICATE USER */
  setUserContext(user: User) {
    if (process.env.NODE_ENV === 'production') {
      if (this.Raven) {
        this.Raven.setUserContext({
          id: user.id,
          email: user.email,
        });
      }
      /*if (this.mixpanel) {
        this.mixpanel.identify(id);
      }*/
    }
    if (this.analytics) {
      this.analytics.identify(user.id, {
        email: user.email,
        first_name: user.firstName,
        last_name: user.lastName,
        name: `${user.firstName} ${user.lastName}`,
        avatar: user.image || '',
        phone: user.phone || '',
        language: user.languageCode,
      });
    }
  }

  unsetUserContext() {
    if (process.env.NODE_ENV === 'production') {
      if (this.Raven) {
        this.Raven.setUserContext();
      }
      /*if (this.mixpanel) {
        this.mixpanel.reset();
      }*/
    }
    if (this.analytics) {
      this.analytics.reset();
    }
  }

  /*afterInitialStateParsedBeforeApplicationHook(rootState: RootState) {
    const auth = rootState.auth || {};
    if (!auth.user || !auth.authenticated) {
      console.error('Could not set Raven context because: Not authenticated');
      return;
    }
    this.setUserContext(auth.user);
  }*/

  /* SENTRY */
  initializeErrorTracking() {
    // if not window defined, then exit
    if (!window) return;
    // Error tracking will only load for production environment
    if (process.env.NODE_ENV === 'production') {
      if (this.Raven) {
        this.Raven.config(environment.sentry, {
          environment: environment.env,
          release: environment.version,
        }).install();
      } else {
        console.error('Could not initialize error tracking because Raven was not found');
      }
    }
  }

  reportError(error: Error, extra?: { [key: string]: any }, req?: { [key: string]: any }) {
    if (process.env.NODE_ENV === 'production') {
      if (this.Raven) {
        const options = {};
        if (!!extra) {
          options.extra = extra;
        }
        if (!!req) {
          options.req = req;
        }
        this.Raven.captureException(error, options);
      }
    }
  }

  /* SEGMENT */
  initializeEventTracking = () => {
    // if not window defined, then exit
    if (!window) return;
    // Error tracking will only load for production environment
    // if (process.env.NODE_ENV === 'production') {
    if (window.initGTM) {
      /*window.initGTM(window, document, 'script', 'dataLayer', environment.googleTagManager);
      console.log('GTM init', environment.googleTagManager);*/
      window.initGTM(window, document, 'script', 'dataLayer', environment.googleAnalytics);
      window['gtag']('js', new Date());
      const gtagOpts = {'debug_mode': true, send_page_view: false};
      window['gtag']('config', environment.googleAnalytics, gtagOpts);
      console.log('GA init', environment.googleAnalytics, gtagOpts);
    }
    /*if (window.initSegment) {
      window.initSegment(environment.segment);
    }*/
    if (this.analytics) {
      if (process.env.WEBAPP_ENV === 'production' || process.env.WEBAPP_ENV === 'local') {
        this.analytics.debug(false);
      } else {
        this.analytics.debug();
        // this.analytics.debug();
      }
      this.analytics.ready(() => {
        if (this._analyticsReadyCb) {
          this._analyticsReadyCb();
        }
        // NOTE: add google optimize
        // NOTE: https://community.segment.com/t/63ml9j/google-optimize-within-google-analytics-integration
        // window.ga('require', environment.googleTagManager);
        // Retrieve gaid in a safe way
        window.ga(tracker => {
          window.gaid = tracker.get('clientId');
          if (this._gaReadyCb) {
            this._gaReadyCb();
          }
        });
      });
    }
    /*if (this.mixpanel) {
      this.mixpanel.init(environment.mixpanel, {
        loaded: (mx) => window.mixpanel = mx,
      });
    } else {
      console.error('Could not initialize event tracking because mixpanel was not found');
    }*/
    //}
  };

  pageView(category?: string, properties?: { [key: string]: string }) {
    const executePageView = () => {
      const p = {
        title: `${document.title}`,
        url: `${document.location.href}`,
        path: `${document.location.pathname}`,
        referrer: `${document.referrer}`,
        search: `${document.location.search}`,
      };
      const abProperties = {};

      const abTest = getCookieBasedTest();

      if (abTest) {
        abProperties.abTest = abTest.test;
        abProperties.abTestVariant = abTest.variant;
      }

      if (this.analytics) {
        this.analytics.page(category, { ...p, ...properties, ...abProperties });
      }

      if (this._gtag) {
        this._gtag('event', 'page_view', {page_title: category});
      }

      pageAnalyticsApi({
        pageName: category,
        properties: { ...p, ...properties },
        client: {
          userAgent: window.navigator ? window.navigator.userAgent : '',
          fbid: (window: any).FB ? (window: any).FB.getUserID() : '',
        },
      }).toPromise().then(() => null);
    };

    if (!!window) {
      if (!window.firstTracking) {
        window.pageViewQueue = window.pageViewQueue || [];
        window.pageViewQueue.push(executePageView);
      } else {
        executePageView();
      }
    }
  }

  fireCustomEvent(eventName: string,
                  properties: { [key: string]: any } = {},
                  options: { [key: string]: any } = {}) {
    const context = {
      app: {
        name: 'qempo-webapp',
        version: environment.version,
      },
    };
    const landingUTM = window.localStorage.getItem('landingUTM');
    let parsedLandingUTM;
    try {
      parsedLandingUTM = JSON.parse(landingUTM);
    }catch (e) {
      parsedLandingUTM = {};
    }
    const richOptions = { ...options, context };
    const richProperties = { ...parsedLandingUTM, ...properties };

    const abTest = getCookieBasedTest();

    if (abTest) {
      richOptions.abTest = abTest.test;
      richOptions.abTestVariant = abTest.variant;
    }

    if (this.analytics) {
      this.analytics.track(eventName, richProperties, richOptions);
    }

    if (this._gtag) {
      const formattedEventName = eventName.toLowerCase().replaceAll(' ', '_');
      console.log('[EVENT-GA4]', formattedEventName, richProperties);
      this._gtag('event', formattedEventName, richProperties);
    }

    fireEventAnalyticsApi({
      eventName,
      properties: richProperties,
      client: {
        userAgent: window.navigator ? window.navigator.userAgent : '',
        fbid: (window: any).FB ? (window: any).FB.getUserID() : '',
      },
    }).toPromise().then(() => null);
  }

  analyticsReady = (analyticsReadyCb: () => void) => this._analyticsReadyCb = analyticsReadyCb;

  gaReady = (gaReadyCb: () => void) => this._gaReadyCb = gaReadyCb;

  mapProduct = (product: ScrappedProduct | ShoppingCartItem | BagItem | OrderItem,
                categoriesMap?: { [key: string]: Category }, categoryTrackingLabel: string = 'category'): Product => {
    const p: { [key: string]: string | number } = {
      url: product.url,
      name: product.name,
      currency: 'USD',
      image_url: product.image || '',
      quantity: product.amount || 1,
    };
    const price = parseFloat(product.price);
    const shipping = parseFloat(product.shipping);
    const categoryId = typeof product.itemCategoryId === 'number' ?
      `${product.itemCategoryId}` : `${product.categoryId}`;
    if (!isNaN(price)) {
      p.price = price;
    }
    if (!isNaN(shipping)) {
      p.shipping = shipping;
    }
    if (!!categoriesMap && categoryId && !!categoriesMap[categoryId]) {
      p[categoryTrackingLabel] = categoriesMap[categoryId].seoUrl;
    }
    return (p: any);
  };

  mapProducts(products: (ScrappedProduct | ShoppingCartItem | BagItem | OrderItem)[],
              categoriesMap?: { [key: string]: Category }): Product[] {
    return products.map(
      (p: ScrappedProduct | ShoppingCartItem | BagItem | OrderItem) => this.mapProduct(p, categoriesMap));
  }

  buildTrackingData(localeState: LocaleState, extra?: { [key: string]: string }): TrackingData {

    const abTest = getCookieBasedTest();

    return {
      gaId: this.googleAnalyticsId,
      userReferrer: window.localStorage.getItem('userReferrer') || '',
      landingUrl: window.localStorage.getItem('landingUrl') || '',
      domainCountry: localeState.domainCountry,
      abtest: abTest.test,
      abtestvariant: abTest.variant,
      ...extra,
    };
  }
}

export default new Analytics();



// WEBPACK FOOTER //
// ./src/analytics/index.js